import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  listComments,
  createComment as createCommentApi,
  reactToComment,
  type CommentDTO,
  type CommentTargetType,
  type CommentReaction,
} from '../api/comments'

const incrementReaction = (
  prev: Record<string, number> | undefined,
  next: CommentReaction,
  previousReaction?: string | null
) => {
  const out: Record<string, number> = { ...(prev ?? {}) }
  if (previousReaction && out[previousReaction]) {
    out[previousReaction] = Math.max(0, (out[previousReaction] ?? 0) - 1)
    if (out[previousReaction] === 0) delete out[previousReaction]
  }
  out[next] = (out[next] ?? 0) + 1
  return out
}

const decrementReaction = (prev: Record<string, number> | undefined, reaction: CommentReaction) => {
  const out: Record<string, number> = { ...(prev ?? {}) }
  if (out[reaction]) {
    out[reaction] = Math.max(0, out[reaction] - 1)
    if (out[reaction] === 0) delete out[reaction]
  }
  return out
}

export type UseCommentsTarget = { type: CommentTargetType; id: string | number }

export function useComments(target: UseCommentsTarget, sort: 'top' | 'newest' | 'following' = 'top') {
  const [items, setItems] = useState<CommentDTO[]>([])
  const [cursor, setCursor] = useState<string | null | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const targetRef = useRef(target)
  useEffect(() => {
    targetRef.current = target
  }, [target])

  const load = useCallback(
    async (reset = false) => {
      if (loading) return
      setLoading(true)
      setError(null)
      try {
        const res = await listComments({
          type: targetRef.current.type,
          id: targetRef.current.id,
          cursor: reset ? null : cursor ?? null,
          sort,
        })
        setCursor(res.next_cursor ?? null)
        setItems((prev) => (reset ? res.items : [...prev, ...res.items]))
      } catch (err: any) {
        setError(typeof err?.message === 'string' ? err.message : 'Failed to load comments')
      } finally {
        setLoading(false)
      }
    },
    [cursor, loading, sort]
  )

  useEffect(() => {
    setItems([])
    setCursor(undefined)
    load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.type, target.id, sort])

  const refresh = useCallback(async () => {
    setRefreshing(true)
    await load(true)
    setRefreshing(false)
  }, [load])

  const create = useCallback(async (body: string, parent_id?: string | number | null) => {
    const tmpId = `tmp-${Date.now()}`
    const optimistic: CommentDTO = {
      id: tmpId,
      author: { id: 'me', name: 'You' },
      body,
      body_plain: body,
      created_at: new Date().toISOString(),
      parent_id: parent_id ?? null,
    }
    setItems((prev) => [optimistic, ...prev])

    try {
      const real = await createCommentApi({
        type: targetRef.current.type,
        id: targetRef.current.id,
        body,
        parent_id,
      })
      setItems((prev) => prev.map((item) => (item.id === tmpId ? real : item)))
      return real
    } catch (err) {
      setItems((prev) => prev.filter((item) => item.id !== tmpId))
      throw err
    }
  }, [])

  const react = useCallback(async (comment_id: string | number, reaction: CommentReaction) => {
    let previousState: CommentDTO | undefined
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== comment_id) return item
        previousState = item
        const prevReaction = item.my_reaction ?? null
        const toggledOff = prevReaction === reaction
        return {
          ...item,
          my_reaction: toggledOff ? null : reaction,
          reactions: toggledOff
            ? decrementReaction(item.reactions, reaction)
            : incrementReaction(item.reactions, reaction, prevReaction),
        }
      })
    )
    try {
      const res = await reactToComment({ comment_id, reaction })
      setItems((prev) =>
        prev.map((item) => {
          if (item.id !== comment_id) return item
          return {
            ...item,
            my_reaction: (res as any)?.my_reaction ?? item.my_reaction,
            reactions: (res as any)?.counts ?? item.reactions,
          }
        })
      )
    } catch {
      if (previousState) {
        setItems((prev) => prev.map((item) => (item.id === comment_id ? previousState! : item)))
      }
    }
  }, [])

  const patchItem = useCallback((id: string | number, updater: (current: CommentDTO) => CommentDTO) => {
    setItems((prev) => prev.map((item) => (item.id === id ? updater(item) : item)))
  }, [])

  const canLoadMore = useMemo(() => Boolean(cursor), [cursor])

  return { items, loading, refreshing, error, canLoadMore, loadMore: () => load(false), refresh, create, react, patchItem }
}

